import React, { useState, useEffect } from "react";
import { Eye, Info, Loader2 } from "lucide-react";
import { useAuditLogs } from "@features/onboarding/utility-setup/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import AuditLogFilterPanel from "../components/AuditLogFilterPanel";


// Original API response type
export interface AuditLogItem {
  date: string;
  title: string;
  time: string;
  description: string;
  data: {
    id: number;
    length: number;
    module: number;
    prefix: string;
    utility: number;
    currentno: number;
    isActive: boolean;
    isPrefix: boolean;
    separator: string;
    submodule: number;
    startingno: number;
    dateElement: string;
    formatPreview: string;
    submoduleName: string;
    utilityService: string;
    transformedData: {
      [key: string]: {
        new: string | number;
        old: string | number;
      };
    };
    configurationType: number;
    configurationTypeDisplay: string;
  };
  user: {
    name: string;
    email: string;
    userName: string;
  };
}

// Transformed data type (what actually gets passed to the table)
export interface TransformedAuditLogItem {
  id: string;
  date: string;
  title: string;
  time: string;
  description: string;
  data: {
    id: number;
    submoduleName: string;
    formatPreview: string;
    utilityService: string;
    configurationTypeDisplay: string;
    transformedData: any;
  };
  user: {
    name: string;
    email: string;
    userName: string;
  };
}

interface AuditLogsTableProps {
  onViewLog?: (logItem: AuditLogItem) => void;
}

export const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  onViewLog,
}) => {

  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;

  // ✅ Use direct state management instead of URL params
  const [filters, setFilters] = useState<any>({
    page: 1,
    limit: itemsPerPage,
  });

  // Call API with filters - Now uses server-side pagination and search
  const {
    data: apiResponse,
    isLoading: loading,
    error,
  } = useAuditLogs(filters);

  const handleApplyFilters = (newFilters: Partial<any>) => {
    // ✅ Directly update state which triggers API call
    setFilters((prev: any) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to page 1 when applying filters
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: itemsPerPage,
    });
  };

  // Handle view log details
  const handleView = (logItem: TransformedAuditLogItem) => {
    if (onViewLog) {
      // Convert back to original format if needed for the callback
      const originalFormat: AuditLogItem = {
        date: logItem.date,
        title: logItem.title,
        time: logItem.time,
        description: logItem.description,
        data: {
          id: logItem.data.id,
          submoduleName: logItem.data.submoduleName,
          formatPreview: logItem.data.formatPreview,
          utilityService: logItem.data.utilityService,
          configurationTypeDisplay: logItem.data.configurationTypeDisplay,
          transformedData: logItem.data.transformedData,
          // Add other required fields with defaults
          length: 0,
          module: 0,
          prefix: "",
          utility: 0,
          currentno: 0,
          isActive: true,
          isPrefix: false,
          separator: "",
          submodule: 0,
          startingno: 0,
          dateElement: "",
          configurationType: 0,
        },
        user: logItem.user,
      };
      onViewLog(originalFormat);
    }
  };

  // Define table columns for the TRANSFORMED data type
  const columns: TableColumn<TransformedAuditLogItem>[] = [
    {
      key: "action",
      header: "Action",
      width: "w-32",
      type: "custom",
      render: (_: any, row: TransformedAuditLogItem) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {row.title}
        </span>
      ),
    },
    {
      key: "configuration",
      header: "ID Configuration",
      width: "w-48",
      type: "custom",
      render: (_: any, row: TransformedAuditLogItem) => (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-900">
            {row.data?.submoduleName}
          </div>
        </div>
      ),
    },
    {
      key: "user",
      header: "Modified By",
      width: "w-40",
      type: "custom",
      render: (_: any, row: TransformedAuditLogItem) => (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-900">
            {row.user?.name}
          </div>
          <div className="text-xs text-gray-500">{row.user?.email || "-"}</div>
        </div>
      ),
    },
    {
      key: "datetime",
      header: "Date & Time",
      width: "w-40",
      type: "custom",
      render: (_: any, row: TransformedAuditLogItem) => (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-900">{row.date}</div>
          <div className="text-xs text-gray-500 font-mono">{row.time}</div>
        </div>
      ),
    },
    {
      key: "details",
      header: "Details",
      width: "w-64",
      type: "custom",
      render: (_: any, row: TransformedAuditLogItem) => (
        <div className="space-y-1">
          <div className="text-sm text-gray-900">
            {row.data?.configurationTypeDisplay || "-"}
          </div>
          <div className="text-xs text-gray-500 font-mono">
            {row.data?.formatPreview || "-"}
          </div>
          <div className="text-xs text-blue-600">
            {row.data?.utilityService || "-"}
          </div>
        </div>
      ),
    },
  ];

  // ✅ Handle pagination - Directly updates state
  const handlePageChange = (newPage: number) => {
    setFilters((prev: any) => ({
      ...prev,
      page: newPage,
    }));
  };

  // ✅ Handle search - Directly updates state
  const handleSearch = (searchTerm: string, paramName: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [paramName === "query" ? "search_data" : paramName]:
        searchTerm || undefined,
      page: 1, // Reset to page 1 when searching
    }));
  };

  // Extract paginated data from server response
  const tableData = apiResponse?.results || [];
  const totalCount = apiResponse?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Transform data for display with proper typing
  const transformedData: TransformedAuditLogItem[] = tableData.map(
    (item: any, index: number) => ({
      id: item.data?.id?.toString() || `audit-${index}`,
      date: item.date,
      title: item.title,
      time: item.time,
      description: item.description,
      data: {
        id: item.data?.id,
        submoduleName: item.data?.submoduleName,
        formatPreview: item.data?.formatPreview,
        utilityService: item.data?.utilityService,
        configurationTypeDisplay: item.data?.configurationTypeDisplay,
        transformedData: item.data?.transformedData,
      },
      user: {
        name: item.user?.name,
        email: item.user?.email,
        userName: item.user?.userName,
      },
    })
  );

  return (
    <div className="space-y-4">
      {/* Optional: Add filter panel if you want additional filters */}
      {/* <AuditLogFilterPanel 
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
      /> */}

      <GenericTable
        data={transformedData}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search in audit logs..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={{
          currentPage: filters.page || 1,
          totalPages: totalPages,
          totalItems: totalCount,
          itemsPerPage,
          startItem: ((filters.page || 1) - 1) * itemsPerPage + 1,
          endItem: Math.min((filters.page || 1) * itemsPerPage, totalCount),
        }}
        onPageChange={handlePageChange}
        emptyMessage={
          transformedData.length === 0 && !loading
            ? "No audit logs found for the selected filters"
            : undefined
        }
        rowKey="id"
      />
    </div>
  );
};

export default AuditLogsTable;
